/**
 * The swipe tool's control in the Map Controls panel: a prominent toggle,
 * and only while it is on, a hint plus the chips choosing which of the
 * present event layers the swipe clips.
 */
import React from 'react';
import { useAtom } from 'jotai';
import { Box, Chip, ToggleButton, Tooltip, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { swipeEnabledAtom } from './atoms';
import { useLayerState } from './useLayerState';
import { ACCENT, ACCENT_HOVER, DARK_BLUE, TEXT_DIM, WHITE } from './palette';
import { panelToggleSx } from './panelFields';

const SWIPE_TOOLTIP =
  'Compare the event layers against the basemap: drag across the map to reveal what lies ' +
  'beneath the layers you choose below.';
const SWIPE_HINT =
  'Drag on the map to reveal the basemap beneath the selected layers. The split stays where you ' +
  'release it (anchored to the map) -- drag it or swipe again to adjust, or turn Swipe off to restore.';
/** The chips the toggle reveals, so it can say what it controls. */
const SETTINGS_ID = 'swipe-settings';

export const SwipeControl: React.FC = () => {
  const state = useLayerState();
  const [enabled, setEnabled] = useAtom(swipeEnabledAtom);
  const hasEvents = state.presentEventLeaves.length > 0;

  return (
    <Box>
      {/* The span keeps the tooltip reachable while the toggle is disabled. */}
      <Tooltip title={SWIPE_TOOLTIP} placement="right" describeChild>
        <span>
          <ToggleButton
            value="swipe"
            selected={enabled}
            // Never disabled while ON: the event layers can vanish under an
            // active swipe (model switch), and the toggle must stay the way out.
            disabled={!hasEvents && !enabled}
            aria-label="Toggle swipe mode"
            aria-expanded={enabled && hasEvents}
            aria-controls={enabled && hasEvents ? SETTINGS_ID : undefined}
            onChange={() => setEnabled(v => !v)}
            sx={panelToggleSx}
          >
            <CompareArrowsIcon fontSize="small" />
            Swipe
          </ToggleButton>
        </span>
      </Tooltip>

      {!hasEvents && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: TEXT_DIM }}>
          No event layers on the map yet.
        </Typography>
      )}

      {enabled && hasEvents && (
        <Box id={SETTINGS_ID} sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ display: 'block', color: TEXT_DIM, lineHeight: 1.3 }}>
            {SWIPE_HINT}
          </Typography>
          {/* One row: per-layer chips on the left, "All" pushed to the right. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {state.presentEventLeaves.map(leaf => {
              const on = state.isInSwipe(leaf.key);
              return (
                <Chip
                  key={leaf.key}
                  label={leaf.label}
                  size="small"
                  clickable
                  aria-label={`${on ? 'Remove from' : 'Add to'} swipe: ${leaf.label}`}
                  aria-pressed={on}
                  onClick={() => state.toggleSwipe(leaf.key)}
                  sx={{
                    height: 24,
                    fontWeight: on ? 700 : 400,
                    color: on ? DARK_BLUE : WHITE,
                    backgroundColor: on ? ACCENT : 'transparent',
                    border: `1px solid ${on ? ACCENT : 'rgba(255,255,255,0.6)'}`,
                    '&:hover': { backgroundColor: on ? ACCENT_HOVER : 'rgba(255,255,255,0.12)' },
                  }}
                />
              );
            })}
            <Chip
              icon={<CompareArrowsIcon fontSize="small" />}
              label="All"
              size="small"
              clickable
              aria-label={
                state.swipeAllSelected ? 'Deselect all event layers' : 'Swipe all event layers'
              }
              aria-pressed={state.swipeAllSelected}
              onClick={() => state.toggleAllSwipe()}
              sx={{
                ml: 'auto',
                height: 24,
                fontWeight: 700,
                color: state.swipeAllSelected ? DARK_BLUE : WHITE,
                backgroundColor: state.swipeAllSelected ? ACCENT : 'transparent',
                border: `1px solid ${state.swipeAllSelected ? ACCENT : 'rgba(255,255,255,0.6)'}`,
                '& .MuiChip-icon': { color: 'inherit' },
                '&:hover': {
                  backgroundColor: state.swipeAllSelected ? ACCENT_HOVER : 'rgba(255,255,255,0.12)',
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
